import type { Ref } from 'vue'
import { scheduleApi } from '@/api/schedule'
import { markErrorSilent } from '@/utils/error-handler'
import { didScheduleStartupErrorRefresh } from '@/utils/scheduleStartupError'
import type {
  ScheduleRunDetail,
  ScheduleTaskDetail,
  ScheduleTaskRunRequest
} from '@/types'

type RunActionsDeps = {
  currentTask: Ref<ScheduleTaskDetail | null>
  latestRun: Ref<ScheduleRunDetail | null>
  running: Ref<boolean>
  refreshCurrentTask: (taskId?: number) => Promise<void>
  fetchTasks: (silent?: boolean) => Promise<void>
}

/**
 * 排课运行控制 actions。逻辑从 schedule store 原样迁出。
 */
export function useScheduleRunActions(deps: RunActionsDeps): {
  startRun: (payload?: ScheduleTaskRunRequest) => Promise<ScheduleRunDetail | null>
  cancelTaskScheduling: (taskId: number) => Promise<void>
  requestLatestRunSnapshot: () => Promise<ScheduleRunDetail | null>
} {
  const { currentTask, latestRun, running, refreshCurrentTask, fetchTasks } = deps

  async function startRun(payload?: ScheduleTaskRunRequest): Promise<ScheduleRunDetail | null> {
    if (!currentTask.value) {
      return null
    }

    running.value = true
    const taskId = currentTask.value.id
    const beforeStart = currentTask.value

    try {
      try {
        await scheduleApi.startRun(taskId, payload)
      } catch (err) {
        // 只在后端确实写入了本次启动拦截时切到持久化展示；网络/认证错误仍正常抛出。
        await fetchTasks(true)
        await refreshCurrentTask(taskId)
        if (didScheduleStartupErrorRefresh(currentTask.value, beforeStart)) {
          throw markErrorSilent(err)
        }
        throw err
      }
      await refreshCurrentTask(taskId)
      return latestRun.value
    } finally {
      running.value = false
    }
  }

  async function cancelTaskScheduling(taskId: number): Promise<void> {
    await scheduleApi.cancelActiveScheduling(taskId)
    await fetchTasks(true)
  }

  async function requestLatestRunSnapshot(): Promise<ScheduleRunDetail | null> {
    if (!currentTask.value || !latestRun.value) {
      return null
    }

    await scheduleApi.requestSnapshot(currentTask.value.id, latestRun.value.id)
    await refreshCurrentTask(currentTask.value.id)
    return latestRun.value
  }

  return {
    startRun,
    cancelTaskScheduling,
    requestLatestRunSnapshot
  }
}
